import type { FocusEvent, ReactNode } from 'react';
import type { ClientResponse } from '@/core/generated/api';
import { clientService } from '../api/clientService';
import type { AsyncSearchOption } from '@/shared/ui/AsyncSearchField';
import { PersonSearchField } from '@/shared/ui/PersonSearchField/PersonSearchField';
import { ClientOptionRow } from './ClientOptionRow';
import { getClientDisplayName } from '../model/displayName';
import { normalizePersonSearchQuery } from '@/shared/lib/phone';

export type ClientSearchOption = AsyncSearchOption<string, ClientResponse>;

interface ClientSearchFieldProps {
    name: string;
    label: string;
    value: string | null;
    onChange: (clientId: string | null, client: ClientResponse | null) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    itemTemplate?: (option: ClientSearchOption) => ReactNode;
    error?: string;
    disabled?: boolean;
    isRequired?: boolean;
    placeholder?: string;
    minSearchLength?: number;
    debounceMs?: number;
    onClientResolved?: (client: ClientResponse | null) => void;
    panelClassName?: string;
    scrollHeight?: string;
    takeCount?: number;
}

/** Формирует понятную подпись клиента для поля выбора */
const getClientOptionName = (client: ClientResponse): string => (
    [getClientDisplayName(client), client.phone || client.appUserPhone].filter(Boolean).join(' · ') || 'Клиент без имени'
);

/** Преобразует найденного клиента в вариант поиска */
const toClientOption = (client: ClientResponse): ClientSearchOption | null => (
    client.id
        ? {
            id: client.id,
            name: getClientOptionName(client),
            data: client
        }
        : null
);

/** Загружает ограниченный список клиентов по имени или телефону */
const searchClientOptions = async (
    query: string,
    takeCount: number
): Promise<ClientSearchOption[]> => (
    (await clientService.getList({
        searchQuery: normalizePersonSearchQuery(query),
        takeCount,
        skipCount: 0,
    })).clients
        ?.map(toClientOption)
        .filter((option): option is ClientSearchOption => option !== null)
        .slice(0, takeCount) ?? []
);

/** Восстанавливает выбранного клиента по идентификатору */
const resolveClientOption = async (id: string): Promise<ClientSearchOption | null> => (
    toClientOption(await clientService.getById(id))
);

/** Предоставляет поиск клиента */
export const ClientSearchField = (
    {
        name,
        label,
        value,
        onChange,
        onBlur,
        itemTemplate,
        error,
        disabled = false,
        isRequired = false,
        placeholder = 'Введите имя клиента',
        minSearchLength = 2,
        debounceMs = 400,
        onClientResolved,
        panelClassName,
        scrollHeight,
        takeCount = 12,
    }: ClientSearchFieldProps
) => {

    return <PersonSearchField<ClientResponse>
        name={name}
        label={label}
        value={value}
        onChange={(clientId, option) => onChange(clientId, option?.data ?? null)}
        onBlur={onBlur}
        loadOptions={(query) => searchClientOptions(query, takeCount)}
        resolveOption={resolveClientOption}
        onOptionResolved={(option) => onClientResolved?.(option?.data ?? null)}
        itemTemplate={itemTemplate ?? ((option) => option.data
            ? <ClientOptionRow client={option.data} />
            : option.name)}
        selectedItemTemplate={(option) => option.name}
        panelClassName={panelClassName}
        scrollHeight={scrollHeight}
        error={error}
        disabled={disabled}
        isRequired={isRequired}
        placeholder={placeholder}
        minSearchLength={minSearchLength}
        debounceMs={debounceMs}
        emptyMessage="Клиенты не найдены"
        searchErrorMessage="Не удалось найти клиента"
    />;
};
